import BotonManualHeader from "../../components/BotonManualHeader";
import React, { useState, useEffect } from "react";
import {
    Container,
    Card,
    Table,
    Badge,
    Button,
    Row,
    Col,
    Spinner,
    Alert,
    ProgressBar
} from "react-bootstrap";
import {
    FaExclamationTriangle,
    FaClock,
    FaBell,
    FaMoneyBillWave,
    FaUserClock,
    FaCheckCircle
} from "react-icons/fa";
import {
    getFirestore,
    collection,
    query,
    where,
    getDocs,
    addDoc,
    serverTimestamp
} from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import Swal from "sweetalert2";

const db = getFirestore(firebaseApp);

function AlertasVacaciones({ usuario }) {
    const [loading, setLoading] = useState(false);
    const [alertas, setAlertas] = useState([]);
    const [resumen, setResumen] = useState({
        total: 0,
        criticas: 0,
        advertencia: 0,
        normal: 0,
        costoRiesgo: 0
    });

    useEffect(() => {
        cargarAlertas();
    }, [usuario]);

    const cargarAlertas = async () => {
        setLoading(true);
        try {
            const ruc = usuario.ruc || usuario.rucEmpresa;

            // 1. Obtener empleados activos
            const usersRef = collection(db, "users");
            const qUsers = query(
                usersRef,
                where("rucEmpresa", "==", ruc),
                where("activo", "==", true)
            );
            const usersSnap = await getDocs(qUsers);
            const empleados = usersSnap.docs.map((doc) => ({
                id: doc.id,
                ...doc.data()
            }));

            const todasAlertas = [];
            let criticas = 0;
            let advertencia = 0;
            let normal = 0;
            let costoRiesgo = 0;

            // 2. Iterar empleados y buscar sus vacaciones pendientes
            // Nota: Idealmente esto se haría con collectionGroup si los docs tuvieran rucEmpresa
            // Para asegurar consistencia, iteramos por empleado (aunque es más lento, es seguro sin índices extra)

            const promesas = empleados.map(async (emp) => {
                const pendientesRef = collection(db, `vacaciones/${emp.dni}/pendientes`);
                const pendientesSnap = await getDocs(pendientesRef);

                pendientesSnap.forEach((doc) => {
                    const data = doc.data();
                    const diasGanados = Number(data.diasGanados) || 0;
                    // Aquí deberíamos restar los gozados, pero por simplicidad de la alerta asumo pendiente bruto
                    // o idealmente el documento 'pendientes' ya debería tener el saldo actualizado.
                    // Basado en VacacionesDetalle, se calcula dinámicamente. 
                    // Para esta versión v1, usaremos los días ganados como base del riesgo.

                    if (diasGanados > 0 && data.fechaVencimiento) {
                        const vencimiento = data.fechaVencimiento.toDate
                            ? data.fechaVencimiento.toDate()
                            : new Date(data.fechaVencimiento);

                        const hoy = new Date();
                        const diffTime = vencimiento - hoy;
                        const diasRestantes = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                        let estado = "normal";
                        let color = "success";

                        if (diasRestantes < 0) {
                            estado = "vencido";
                            color = "danger";
                            criticas++;
                            // Estimación simple: Sueldo / 30 * dias * 2 (indemnización)
                            const sueldo = Number(emp.sueldo) || 1025; // Default mínimo
                            costoRiesgo += (sueldo / 30) * diasGanados * 2;
                        } else if (diasRestantes <= 60) {
                            estado = "proximo";
                            color = "warning";
                            advertencia++;
                        } else {
                            normal++;
                        }

                        if (estado !== "normal") {
                            todasAlertas.push({
                                id: doc.id,
                                empleado: emp.nombreCompleto || emp.nombre || data.nombreCompleto || "Empleado Desconocido",
                                dni: emp.dni,
                                area: emp.area || "General",
                                periodo: data.periodo,
                                dias: diasGanados,
                                vencimiento,
                                diasRestantes,
                                estado,
                                color,
                                sueldo: emp.sueldo
                            });
                        }
                    }
                });
            });

            await Promise.all(promesas);

            // Ordenar por urgencia (vencidos primero)
            todasAlertas.sort((a, b) => a.diasRestantes - b.diasRestantes);

            setAlertas(todasAlertas);
            setResumen({
                total: todasAlertas.length,
                criticas,
                advertencia,
                normal,
                costoRiesgo
            });

        } catch (error) {
            console.error("Error al cargar alertas:", error);
            Swal.fire("Error", "No se pudieron cargar las alertas de vacaciones", "error");
        } finally {
            setLoading(false);
        }
    };

    const enviarRecordatorio = async (alerta) => {
        try {
            // Simular envío de notificación
            // Aquí se conectaría con notificacionesService
            const notifRef = collection(db, `notificaciones/${alerta.dni}/inbox`);
            await addDoc(notifRef, {
                tipo: "vacaciones_por_vencer",
                prioridad: alerta.estado === "vencido" ? "urgente" : "alta",
                titulo: "Recordatorio de Vacaciones",
                mensaje: `Tienes vacaciones del periodo ${alerta.periodo} que vencen el ${alerta.vencimiento.toLocaleDateString()}. Contacta a RRHH.`,
                leida: false,
                createdAt: serverTimestamp(),
                rucEmpresa: usuario.ruc || usuario.rucEmpresa
            });

            Swal.fire({
                icon: "success",
                title: "Recordatorio Enviado",
                text: `Se ha notificado a ${alerta.empleado}`,
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            console.error("Error enviando notificación:", error);
            Swal.fire("Error", "No se pudo enviar la notificación", "error");
        }
    };

    const enviarRecordatoriosMasivos = async () => {
        const result = await Swal.fire({
            title: "¿Enviar recordatorios masivos?",
            text: `Se enviarán notificaciones a ${alertas.length} empleados.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Sí, enviar",
            cancelButtonText: "Cancelar"
        });

        if (result.isConfirmed) {
            setLoading(true);
            try {
                const promesas = alertas.map(alerta => enviarRecordatorio(alerta));
                await Promise.all(promesas);
                Swal.fire("Enviados", "Se han enviado todos los recordatorios", "success");
            } catch (error) {
                Swal.fire("Error parcial", "Algunos recordatorios pudieron fallar", "error");
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <div
            style={{
                background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
                minHeight: "100vh",
                width: "100vw",
                maxWidth: "100%",
                padding: "20px 0"
            }}
        >
            <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
                <Card
                    className="shadow-lg border-0"
                    style={{ borderRadius: "15px", overflow: "hidden" }}
                >
                    <Card.Header
                        className="text-white py-4 position-relative"
                        style={{
                            background: "linear-gradient(45deg, #2c3e50, #3498db)",
                            borderBottom: "3px solid #f39c12"
                        }}
                    >
          <div
            className="position-absolute end-0 top-50 translate-middle-y me-4"
            style={{ zIndex: 10 }}
          >
            <BotonManualHeader />
          </div>
                        <div className="text-center">
                            <h2 className="fw-bold mb-1">
                                <FaBell className="me-3" />
                                Alertas de Vacaciones
                            </h2>
                            <p className="mb-0 opacity-75">
                                Monitoreo de vencimientos y riesgos legales
                            </p>
                        </div>
                    </Card.Header>

                    <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>

                        {/* Resumen de KPIs */}
                        <Row className="mb-4">
                            <Col md={3}>
                                <Card className="shadow-sm border-0 text-white bg-danger h-100">
                                    <Card.Body className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <h6 className="opacity-75">Vencidas (Crítico)</h6>
                                            <h2 className="fw-bold mb-0">{resumen.criticas}</h2>
                                        </div>
                                        <FaExclamationTriangle size={40} className="opacity-50" />
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="shadow-sm border-0 text-white bg-warning h-100">
                                    <Card.Body className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <h6 className="opacity-75">Por Vencer (60 días)</h6>
                                            <h2 className="fw-bold mb-0 text-dark">{resumen.advertencia}</h2>
                                        </div>
                                        <FaClock size={40} className="opacity-50 text-dark" />
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="shadow-sm border-0 text-white bg-primary h-100">
                                    <Card.Body className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <h6 className="opacity-75">Total Alertas</h6>
                                            <h2 className="fw-bold mb-0">{resumen.total}</h2>
                                        </div>
                                        <FaUserClock size={40} className="opacity-50" />
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="shadow-sm border-0 text-white bg-dark h-100">
                                    <Card.Body className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <h6 className="opacity-75">Riesgo Financiero Est.</h6>
                                            <h4 className="fw-bold mb-0">S/ {resumen.costoRiesgo.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</h4>
                                        </div>
                                        <FaMoneyBillWave size={40} className="opacity-50" />
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Acciones */}
                        <div className="d-flex justify-content-end mb-3">
                            <Button
                                variant="outline-primary"
                                onClick={cargarAlertas}
                                disabled={loading}
                                className="me-2"
                            >
                                Actualizar
                            </Button>
                            <Button
                                variant="danger"
                                onClick={enviarRecordatoriosMasivos}
                                disabled={loading || alertas.length === 0}
                            >
                                <FaBell className="me-2" />
                                Notificar a Todos
                            </Button>
                        </div>

                        {/* Tabla de Alertas */}
                        <Card className="shadow-sm border-0">
                            <Card.Body className="p-0">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" variant="primary" />
                                        <p className="mt-2 text-muted">Analizando vencimientos...</p>
                                    </div>
                                ) : (
                                    <Table responsive hover className="mb-0 align-middle">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Empleado</th>
                                                <th>Periodo</th>
                                                <th>Días</th>
                                                <th>Vencimiento</th>
                                                <th>Estado</th>
                                                <th>Acción</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {alertas.length === 0 ? (
                                                <tr>
                                                    <td colSpan={6} className="text-center py-4 text-success">
                                                        <FaCheckCircle size={30} className="mb-2" />
                                                        <p className="mb-0">¡Excelente! No hay vacaciones críticas pendientes.</p>
                                                    </td>
                                                </tr>
                                            ) : (
                                                alertas.map((alerta, idx) => (
                                                    <tr key={idx}>
                                                        <td>
                                                            <div className="fw-bold">{alerta.empleado}</div>
                                                            <small className="text-muted">{alerta.area}</small>
                                                        </td>
                                                        <td>{alerta.periodo}</td>
                                                        <td>
                                                            <Badge bg="info">{alerta.dias} días</Badge>
                                                        </td>
                                                        <td>
                                                            <div>{alerta.vencimiento.toLocaleDateString()}</div>
                                                            <small className={
                                                                alerta.diasRestantes < 0 ? "text-danger fw-bold" : "text-muted"
                                                            }>
                                                                {alerta.diasRestantes < 0
                                                                    ? `Vencido hace ${Math.abs(alerta.diasRestantes)} días`
                                                                    : `Vence en ${alerta.diasRestantes} días`}
                                                            </small>
                                                        </td>
                                                        <td>
                                                            <Badge bg={alerta.color} className="text-uppercase">
                                                                {alerta.estado === 'vencido' ? 'Crítico' : 'Próximo'}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <Button
                                                                variant="outline-dark"
                                                                size="sm"
                                                                onClick={() => enviarRecordatorio(alerta)}
                                                            >
                                                                <FaBell className="me-1" /> Notificar
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                )}
                            </Card.Body>
                        </Card>

                    </Card.Body>
                </Card>
            </Container>
        </div>
    );
}

export default AlertasVacaciones;
